
import { hygraphOperation } from "@/mixins/hygraph";
import { hygraphSrcset } from "@/utils/hygraphImage";
import { BLOG_FONT_PRELOADS } from "@/utils/fontPreloads";
import { WIDE_SIZES, WIDE_WIDTHS } from "@/components/blog/imageSlots";
import { pillarLabel } from "@/utils/pillars";
import { stripRedundantLinkTitles } from "@/utils/proseHtml";
import ProseBlock from "@/components/blog/blocks/ProseBlock.vue";
import H2Block from "@/components/blog/blocks/H2Block.vue";
import ImageBlock from "@/components/blog/blocks/ImageBlock.vue";
import Blockquote from "@/components/blog/blocks/Blockquote.vue";
import ImagePair from "@/components/blog/blocks/ImagePair.vue";
import VideoEmbed from "@/components/blog/blocks/VideoEmbed.vue";
import DataTable from "@/components/blog/blocks/DataTable.vue";
import MidCTA from "@/components/blog/blocks/MidCTA.vue";
import Tldr from "@/components/blog/modules/Tldr.vue";
import TableOfContents from "@/components/blog/modules/TableOfContents.vue";
import FaqAccordion from "@/components/blog/modules/FaqAccordion.vue";
import BottomCta from "@/components/blog/BottomCta.vue";
import AboutLeisureCraft from "@/components/blog/AboutLeisureCraft.vue";
import FurtherReading from "@/components/blog/FurtherReading.vue";
import { toIsoDate } from "@/utils/jsonldSafe";
import articleJsonld from "@/mixins/jsonld/article";
import faqpageJsonld from "@/mixins/jsonld/faqpage";
import breadcrumbsBlogJsonld from "@/mixins/jsonld/breadcrumbs-blog";
// Organization schema is emitted on the homepage only (layouts/default.vue) —
// Google expects one per site. This page carries its own publisher inside the
// Article schema, so it does not need a copy.

export default {
  components: {
    ProseBlock,
    H2Block,
    ImageBlock,
    Blockquote,
    ImagePair,
    VideoEmbed,
    DataTable,
    MidCTA,
    Tldr,
    TableOfContents,
    FaqAccordion,
    BottomCta,
    AboutLeisureCraft,
    FurtherReading,
  },

  mixins: [
    articleJsonld,
    faqpageJsonld,
    breadcrumbsBlogJsonld,
  ],

  meta: {
    blocks: {
      unwind: {
        public: true,
        dealer: false,
      },
    },
  },

  data() {
    return {
      article: null,
      siteSettings: null,
      WIDE_SIZES,
    };
  },

  computed: {
    // True when this same component is mounted at /blog-draft-preview/{slug}
    // (the dealer-admin-gated route, see pages/blog-draft-preview/_slug.vue).
    // Drives the banner + ensures canonical still points to the public /blog URL.
    isPreview() {
      return this.$route.path.startsWith("/blog-draft-preview/");
    },

    SEO() {
      return {
        title: this.article?.seoTitle || this.article?.title,
        description: this.article?.seoDescription || this.article?.deck,
        image: this.socialImage,
        type: "article",
        publishedTime: toIsoDate(this.article?.articlePublishedAt),
        modifiedTime: toIsoDate(this.article?.articleUpdatedAt),
        author: this.article?.author?.name,
        // Public route is indexed; the preview route (same component, gated)
        // forces noindex,nofollow so reviewers' shared URLs never leak.
        index: !this.isPreview,
        canonical: `/blog/${this.$route.params.slug}`,
        // The blog faces are declared sitewide in assets/scss/fonts.scss; only
        // the preload hints are page-scoped (see utils/fontPreloads.js).
        // bodyAttrs.class sets the blog-bg-page bg without touching the
        // site-wide --white var.
        options: {
          bodyAttrs: { class: "blog-bg-page" },
          link: BLOG_FONT_PRELOADS,
        },
        scripts: [
          this.articleJsonldScript,
          this.faqPageJsonldScript,
          this.breadcrumbsBlogJsonldScript,
        ].filter(Boolean),
      };
    },

    heroSrcset() {
      return hygraphSrcset(this.article?.heroImage, WIDE_WIDTHS);
    },

    // Rendered at 46px; 3x covers the densest phone screens.
    avatarSrcset() {
      return hygraphSrcset(this.article?.author?.avatar, [46, 92, 138]);
    },

    socialImage() {
      return (
        this.article?.socialShareImage?.url ||
        this.article?.heroImage?.url ||
        this.siteSettings?.defaultShareImage?.url
      );
    },

    pillarLabelText() {
      return pillarLabel(this.article);
    },

    publishedLabel() {
      return this.formatDate(this.article?.articlePublishedAt);
    },

    updatedLabel() {
      return this.formatDate(this.article?.articleUpdatedAt);
    },

    // Normalize the modular body into clean block objects and assign
    // H2 numbering (01/02…) across H2Block instances in document order.
    // Aliasing/quirks of the GraphQL union live here so block components
    // stay dumb and read tidy props.
    renderedBody() {
      const KNOWN = [
        "ProseBlock",
        "H2Block",
        "ImageBlock",
        "Blockquote",
        "ImagePair",
        "VideoEmbed",
        "DataTable",
        "MidCTA",
      ];
      let h2 = 0;
      return (this.article?.body || [])
        .map((raw) => {
          const t = raw.__typename;
          if (t === "ProseBlock") {
            return {
              __typename: t,
              html: stripRedundantLinkTitles(raw.content && raw.content.html),
            };
          }
          if (t === "H2Block") {
            h2 += 1;
            return {
              __typename: t,
              num: String(h2).padStart(2, "0"),
              eyebrowLabel: raw.eyebrowLabel,
              heading: raw.heading,
              anchor: raw.anchor,
            };
          }
          if (t === "ImageBlock") {
            return {
              __typename: t,
              // Whole asset, not just its url — the block needs width/height/
              // size to build a srcset (see utils/hygraphImage.js).
              image: raw.image || null,
              alt: raw.alt,
              caption: raw.caption,
              aspectRatio: raw.aspectRatio,
              width: raw.width,
            };
          }
          if (t === "Blockquote") {
            return {
              __typename: t,
              heading: raw.bqHeading,
              body: raw.body,
              capsule: raw.bqHeading === "Key answer",
            };
          }
          if (t === "ImagePair") {
            return {
              __typename: t,
              leftImage: raw.leftImage || null,
              leftAlt: raw.leftAlt,
              rightImage: raw.rightImage || null,
              rightAlt: raw.rightAlt,
              caption: raw.pairCaption,
            };
          }
          if (t === "VideoEmbed") {
            return {
              __typename: t,
              videoId: raw.videoId,
              title: raw.title,
              caption: raw.caption,
              thumbnailOverride: raw.thumbnailOverride || null,
              aspectRatio: raw.vidAspectRatio,
            };
          }
          if (t === "DataTable") {
            return {
              __typename: t,
              headers: raw.headers,
              rows: raw.rows,
              caption: raw.caption,
            };
          }
          if (t === "MidCTA") {
            return {
              __typename: t,
              headline: raw.headline,
              body: raw.body,
              buttonLabel: raw.buttonLabel,
              buttonUrl: raw.buttonUrl,
              buttonStyle: raw.buttonStyle,
            };
          }
          return { __typename: t };
        })
        .filter((b) => KNOWN.includes(b.__typename));
    },

    // Intro = the first body block when it's prose; the design places
    // TL;DR + TOC right after it, before the rest of the body.
    introBody() {
      const first = this.renderedBody[0];
      return first && first.__typename === "ProseBlock" ? [first] : [];
    },

    mainBody() {
      return this.introBody.length
        ? this.renderedBody.slice(1)
        : this.renderedBody;
    },

    hasTldr() {
      const t = this.article && this.article.tldrItems;
      return Array.isArray(t) && t.length > 0;
    },

    hasToc() {
      const t = this.article && this.article.tocItems;
      return Array.isArray(t) && t.length > 0;
    },

    hasAnswerCapsule() {
      const s = this.article && this.article.answerCapsule;
      return typeof s === "string" && s.trim().length > 0;
    },

    hasFaq() {
      const f = this.article && this.article.faqItems;
      return Array.isArray(f) && f.length > 0;
    },

    // FAQ eyebrow number continues the section count (body H2s + 1).
    faqNumber() {
      const h2 = this.renderedBody.filter(
        (b) => b.__typename === "H2Block"
      ).length;
      return String(h2 + 1).padStart(2, "0");
    },

    bottomCta() {
      const a = this.article || {};
      return {
        headline: a.bottomCtaHeadline,
        body: a.bottomCtaBody,
        primaryLabel: a.bottomCtaPrimaryLabel,
        primaryUrl: a.bottomCtaPrimaryUrl,
        secondaryLabel: a.bottomCtaSecondaryLabel,
        secondaryUrl: a.bottomCtaSecondaryUrl,
      };
    },

    hasFurtherReading() {
      const f = this.article && this.article.furtherReading;
      return Array.isArray(f) && f.length > 0;
    },
  },

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
    },

    // Update the bar imperatively (no reactive state) so scrolling never
    // triggers a re-render — a re-render would wipe the .in-view class
    // the IntersectionObserver adds to images via classList.
    onScroll() {
      const bar = this.$refs.progress;
      if (!bar) return;
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      const ratio = max > 0 ? Math.min(1, Math.max(0, el.scrollTop / max)) : 0;
      bar.style.transform = `scaleX(${ratio})`;
    },

    // Photo Bloom: fade + scale images in as they enter the viewport.
    // .reveal-on is hardcoded on the article root in the template so
    // SSR paints all .reveal images at opacity:0 from frame one. We
    // then observe every .reveal — the IO fires (async) for elements
    // already intersecting too, so an image already on screen blooms
    // on load rather than staying hidden. CSS @media (prefers-reduced-motion)
    // forces opacity:1 regardless, so we just skip setup in that case.
    //
    // The hero is intentionally excluded (no .reveal): an element painted at
    // opacity 0 is not an LCP candidate, so blooming it made the largest paint
    // wait for hydration. Anything added above the fold should think twice
    // before taking this class.
    initReveal() {
      const root = this.$el;
      if (!root) return;
      const reduce =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduce || !("IntersectionObserver" in window)) return;

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("in-view");
              io.unobserve(e.target);
            }
          });
        },
        { rootMargin: "0px 0px -10% 0px" }
      );
      this._revealIO = io;
      root.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    },
  },

  mounted() {
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.onScroll();
    this.initReveal();
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
    if (this._revealIO) this._revealIO.disconnect();
  },

  // Public /blog/{slug} reads PUBLISHED; the dealer-admin-gated
  // /blog-draft-preview/{slug} reads DRAFT (the same HYGRAPH_AUTH_TOKEN
  // already has draft scope).
  async asyncData({ route, error }) {
    const slug = route.params.slug;
    if (!slug) {
      error({ statusCode: 404, message: "Article not found" });
      return;
    }

    try {
      // Query text lives in utils/hygraphQueries.js; the browser only ever names
      // the operation (mixins/hygraph.js). DRAFT resolves server-side only, so
      // /blog-draft-preview forces a full page load rather than a client
      // transition — see middleware/force-server-render.js.
      const isPreview = route.path.startsWith("/blog-draft-preview/");
      const data = await hygraphOperation(
        "blog:article",
        { stage: isPreview ? "DRAFT" : "PUBLISHED", slug },
        // A DRAFT read is denied author, share image and the Asset metadata
        // fields, which used to fail the whole query and show the error page
        // instead of the draft being reviewed. Render what came back and log the
        // rest; the public article stays strict.
        { allowPartial: isPreview }
      );

      const article = data.articles?.[0];

      if (!article) {
        error({ statusCode: 404, message: "Article not found" });
        return;
      }

      return {
        article,
        siteSettings:
          (data.siteSettingsItems && data.siteSettingsItems[0]) || null,
      };
    } catch (err) {
      error({ statusCode: 500, message: "Failed to load article" });
    }
  },
};
